import React, { useRef, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { observer } from 'mobx-react';
import { mdiClose } from '@mdi/js';
import { AdminProjectOwnerVM } from './AdminProjectOwnerVM';
import { Row, Col } from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';
import { AdminProjectOwnerDetails } from '../AdminProjectOwnerDetails/AdminProjectOwnerDetails';
import { AdminProjectOwners } from '../AdminProjectOwners/AdminProjectOwners';
import { InputSearch } from 'cd-eft/components/common/InputSearch';
import { SelectOption } from 'cd-eft/components/common/SelectOption';
import { BreadcrumbsControlled } from 'cd-eft/components/common/BreadcrumbsControlled';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import Icon from '@mdi/react';
import './AdminProjectOwner.scss';

export const AdminProjectOwner = observer(() => {
  const { userId }: any = useParams();
  const history = useHistory();
  const vm = useRef(new AdminProjectOwnerVM(history)).current;

  useEffect(() => {
    vm.init();
  }, []);

  useEffect(() => {
    vm.getUserProjects(userId);
  }, [userId, vm.userData.length]);

  useEffect(() => {
    if (vm.actionOption === 'changeOwner') vm.openModalChangeOwner(vm.selectedUser);
  }, [vm.actionOption]);

  return (
    <div className="admin-content">
      {vm.isLoading && <Loader message="Loading..." />}

      <h1>Admin : Project Owner</h1>

      {userId && (
        <BreadcrumbsControlled
          crumbs={[{ description: 'Back', url: '/admin/project-owner' }, { description: vm.selectedUser.userName }]}
        />
      )}

      <div className="usa-card__container admin-projectOwner-container m-0">
        <div className="usa-card__header primary-card-header">
          <h2 tabIndex={0}>NETS Projects</h2>

          {userId && (
            <div className="d-flex flex-align-center">
              <button className="usa-button usa-button--unstyled clear-all-button" onClick={vm.onClickClearAll}>
                Clear all
              </button>
              <SelectOption
                disabled={vm.projectsToChange.size === 0}
                removeDefaultOption={true}
                idAndName="changeOwnerForProjects"
                options={[
                  {
                    value: '',
                    name: 'action',
                    label: 'Action',
                  },
                  {
                    value: 'changeOwner',
                    name: 'changeOwner',
                    label: 'Change Owner',
                  },
                  {
                    value: 'export',
                    name: 'export',
                    label: 'Export Selection as CSV',
                  },
                ]}
                value={vm.actionOption}
                onChange={vm.setActionOption}
                className="pr-2 m-0"
              />
            </div>
          )}
        </div>

        <div className="d-flex mx-3 mt-3">
          <InputSearch
            name="searchString"
            setSearchValue={userId ? vm.searchByProjectName : vm.searchByUserName}
            searchString={userId ? vm.projectNameSearchString : vm.userNameSearchString}
            placeholder={userId ? 'Search by Project Name' : 'Search by Project Owner Full Name'}
          />
          {userId && <button className="usa-button ml-3">Filter</button>}
        </div>

        {userId ? (
          <AdminProjectOwnerDetails
            projects={vm.selectedUserFilteredProjects}
            projectsToChange={vm.projectsToChange}
            setProjectToChange={vm.setProjectToChange}
            allProjectsSelected={vm.allProjectsSelected}
            setAllProjectsSelected={vm.setAllProjectsSelected}
            clear={vm.clearAll}
          />
        ) : (
          <AdminProjectOwners
            userData={vm.filteredUserData}
            openModalChangeOwner={vm.openModalChangeOwner}
            onClickViewProjects={vm.viewProjects}
          />
        )}
      </div>

      {vm.displayModal && (
        <Modal
          data-testid="change-owner-modal"
          className="project-owner-modal"
          isOpen={vm.displayModal}
          toggle={vm.cancelSelection}
        >
          <ModalHeader>
            <strong>Change Owner</strong>
            <button
              className="usa-button usa-button--unstyled modal-button"
              title="Close"
              onClick={vm.cancelSelection}
            >
              <Icon path={mdiClose} color="black" size={1} />
            </button>
          </ModalHeader>

          {vm.displayConfirmation ? (
            <>
              <ModalBody>
                <div className="mb-1">Are you sure you want to change the Project owner below?</div>
                <Row>
                  <Col>Previous Project Owner</Col>
                  <Col>
                    <strong>{vm.selectedUser.userName}</strong>
                  </Col>
                </Row>
                <Row>
                  <Col>New Project Owner</Col>
                  <Col>
                    <strong>{vm.newOwner.userName}</strong>
                  </Col>
                </Row>
                <Row>
                  <Col># of Project Changed</Col>
                  <Col>
                    <strong>
                      {vm.projectsToChange.size > 0 ? vm.projectsToChange.size : vm.selectedUser.projectCount}
                    </strong>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter className="project-owner-footer m-3">
                <button className="usa-button usa-button--outline" onClick={vm.cancelSelection}>
                  Close
                </button>
                <button className="usa-button" onClick={vm.saveChangingOwner}>
                  Save
                </button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalBody>
                <div>Change Owner</div>
                <div>
                  from <strong>{vm.selectedUser.userName}</strong> to
                </div>
                <div className="search-new-owner-bar">
                  <InputSearch
                    name="newOwnerName"
                    setSearchValue={vm.onChangeNewOwnerName}
                    searchString={vm.newOwnerName}
                    placeholder="Type new project owner first and last name"
                  />

                  {vm.userSuggestions && vm.userSuggestions.length > 0 && (
                    <div data-testid="userSuggestionList" className="userSuggestionList">
                      {vm.userSuggestions.map((user) => (
                        <button key={`suggestion-option-${user.coreUserId}`} onClick={() => vm.selectNewOwner(user)}>
                          {user.userName}
                        </button>
                      ))}
                    </div>
                  )}

                  {vm.userData.findIndex((el) => el.userName === vm.newOwnerName) === -1 &&
                    vm.newOwnerName.length > 0 &&
                    vm.userSuggestions.length === 0 && (
                      <div className="mt-2 ml-2">No user matched your search input</div>
                    )}
                </div>
              </ModalBody>
              <ModalFooter className="project-owner-footer m-3">
                <button className="usa-button usa-button--outline" onClick={vm.cancelSelection}>
                  Close
                </button>
                <button className="usa-button" onClick={vm.saveSelection}>
                  Select
                </button>
              </ModalFooter>
            </>
          )}
        </Modal>
      )}
    </div>
  );
});
